/* eslint-disable complexity */
import React from 'react';

import { UILoader } from '@futrli/ui-components';

import { CATEGORY_NAME_INDEXES } from '../../../../core/constants';
import { ForecastTree, UKForecastLayoutItem, UKForecastLayoutItemDisplayType } from '../types';
import { ForecastUpdating } from '../../components/forecast-updating';
import { getTopLevelStaticCategoryTreeNode } from '../utils/forecast-tree';
import { PredictionsTotalsPageWrapper } from '../predictions-totals-page-wrapper';
import { useOrganisationAccountNames } from '../hooks/useOrganisationAccountNames/useOrganisationAccountNames';
import { usePredictionsInfoContext } from '../../context/predictions-info-context';
import { useUKForecastGrid } from '../hooks/uk/useUKForecastGrid/useUKForecastGrid';

const getLayout = (trees: Record<string, ForecastTree>) => {
  const datasource = 'balance_sheet';
  const tree = trees[datasource];
  const layout: UKForecastLayoutItem[] = [];

  const findNode = (category: CATEGORY_NAME_INDEXES) => {
    return getTopLevelStaticCategoryTreeNode(tree, category);
  };

  const bankAccountsNode = findNode(CATEGORY_NAME_INDEXES.bank_accounts);
  const fixedAssetsNode = findNode(CATEGORY_NAME_INDEXES.fixed_assets);
  const currentAssetsNode = findNode(CATEGORY_NAME_INDEXES.current_assets);
  const nonCurrentAssetsNode = findNode(CATEGORY_NAME_INDEXES.non_current_assets);
  const totalAssetsNode = findNode('total_assets' as CATEGORY_NAME_INDEXES);

  const currentLiabilitiesNode = findNode(CATEGORY_NAME_INDEXES.current_liabilities);
  const nonCurrentLiabilitiesNode = findNode(CATEGORY_NAME_INDEXES.non_current_liabilities);
  const totalLiabilitiesNode = findNode('total_liabilities' as CATEGORY_NAME_INDEXES);
  const netAssetsNode = findNode('net_assets' as CATEGORY_NAME_INDEXES);

  const equityNode = findNode(CATEGORY_NAME_INDEXES.equity);

  if (bankAccountsNode) {
    layout.push({
      category: CATEGORY_NAME_INDEXES.bank_accounts,
      datasource,
      displayType: UKForecastLayoutItemDisplayType.totalWithAccounts,
      nodes: [bankAccountsNode]
    });
  }
  if (fixedAssetsNode) {
    layout.push({
      category: CATEGORY_NAME_INDEXES.fixed_assets,
      datasource,
      displayType: UKForecastLayoutItemDisplayType.totalWithAccounts,
      nodes: [fixedAssetsNode]
    });
  }
  if (currentAssetsNode) {
    layout.push({
      category: CATEGORY_NAME_INDEXES.current_assets,
      datasource,
      displayType: UKForecastLayoutItemDisplayType.totalWithAccounts,
      nodes: [currentAssetsNode]
    });
  }
  if (nonCurrentAssetsNode) {
    layout.push({
      category: CATEGORY_NAME_INDEXES.non_current_assets,
      datasource,
      displayType: UKForecastLayoutItemDisplayType.totalWithAccounts,
      nodes: [nonCurrentAssetsNode]
    });
  }
  if (totalAssetsNode) {
    layout.push({
      category: 'total_assets' as CATEGORY_NAME_INDEXES,
      datasource,
      displayType: UKForecastLayoutItemDisplayType.calculation,
      nodes: [totalAssetsNode]
    });
  }
  if (currentLiabilitiesNode) {
    layout.push({
      category: CATEGORY_NAME_INDEXES.current_liabilities,
      datasource,
      displayType: UKForecastLayoutItemDisplayType.totalWithAccounts,
      nodes: [currentLiabilitiesNode],
      totalOptions: {
        padding: true
      }
    });
  }
  if (nonCurrentLiabilitiesNode) {
    layout.push({
      category: CATEGORY_NAME_INDEXES.non_current_liabilities,
      datasource,
      displayType: UKForecastLayoutItemDisplayType.totalWithAccounts,
      nodes: [nonCurrentLiabilitiesNode]
    });
  }
  if (totalLiabilitiesNode) {
    layout.push({
      category: 'total_liabilities' as CATEGORY_NAME_INDEXES,
      datasource,
      displayType: UKForecastLayoutItemDisplayType.calculation,
      nodes: [totalLiabilitiesNode]
    });
  }
  if (netAssetsNode) {
    layout.push({
      category: 'net_assets' as CATEGORY_NAME_INDEXES,
      datasource,
      displayType: UKForecastLayoutItemDisplayType.calculation,
      nodes: [netAssetsNode],
      options: {
        padding: true
      }
    });
  }

  if (equityNode) {
    layout.push({
      category: CATEGORY_NAME_INDEXES.equity,
      datasource,
      displayType: UKForecastLayoutItemDisplayType.totalWithAccounts,
      nodes: [equityNode]
    });
  }

  return layout;
};

export const BalanceSheetV2 = () => {
  const accountNameMap = useOrganisationAccountNames();
  const { forecast, forecastReportV2: forecastReport, isLoading } = usePredictionsInfoContext();

  const { columns, dataGridRows } = useUKForecastGrid(
    forecast,
    forecastReport,
    accountNameMap,
    getLayout,
    'balanceSheet'
  );

  if (!forecastReport && isLoading) {
    return <UILoader isFull />;
  }

  return (
    <>
      <PredictionsTotalsPageWrapper
        columns={columns}
        totalAccountRows={dataGridRows}
        totalCashPredictRow={null}
        isBalanceSheet
      />
      <ForecastUpdating show={!!isLoading} />
    </>
  );
};
